/* eslint-disable no-underscore-dangle */
import React from 'react';
import PropTypes from 'prop-types';
import { get, size, drop, map, last, first } from 'lodash';
import { TooltipHost } from 'office-ui-fabric-react';
import DetailsList from '../DetailsList';
import styles from './DimensionListsDetails.style';
import Link from '../Link';

const DimensionListsDetails = ({ listName, dimensionItems, listType, createList, ...other }) => {
  const columns = {
    itemName: {
      key: 'listItem',
      name: 'List Item',
      fieldName: 'name',
      columnActionsMode: 0,
      minWidth: 100,
      maxWidth: 300,
      isPadded: true,
      onRender: item => {
        return <div className={styles.text}>{get(item, 'name')}</div>;
      },
    },
    productId: {
      key: 'productId',
      name: 'Product ID',
      fieldName: 'productId',
      isMultiline: true,
      minWidth: 100,
      maxWidth: 200,
      columnActionsMode: 0,
      isPadded: true,
      onRender: item => {
        const otherProductIds = drop(get(item, 'productIds'));
        const productIdsToolTip = otherProductIds.map(product => (
          <span key={product}>
            {product} <br />
          </span>
        ));

        const moreProductIds = size(get(item, 'productIds')) - 1;
        return (
          <div className={styles.productText}>
            {get(item, 'productIds[0]')}
            {moreProductIds > 0 && (
              <TooltipHost
                className={styles.tooltipContent}
                content={productIdsToolTip}
                calloutProps={{
                  gapSpace: 5,
                }}
              >
                <span className={styles.moreProductIds}>
                  {' '}
                  <br /> +{moreProductIds} more
                </span>
              </TooltipHost>
            )}
          </div>
        );
      },
    },
    tokenId: {
      key: 'tokenId',
      name: 'Token ID',
      fieldName: 'tokenId',
      columnActionsMode: 0,
      minWidth: 100,
      maxWidth: listType === 'product' ? 150 : 300,
      isPadded: true,
      onRender: item => {
        return <div className={styles.productText}>{get(item, 'id')} </div>;
      },
    },
    associatedList: {
      key: 'associatedList',
      name: 'Associated List',
      fieldName: 'associatedList',
      columnActionsMode: 0,
      isMultiline: true,
      minWidth: 100,
      maxWidth: 300,
      isPadded: true,
      onRender: item => {
        const listHierarchy = map(get(item, 'listHierarchy'));
        const content = [];
        listHierarchy.forEach(list => {
          const hierarchy = Object.entries(list);
          if (hierarchy.length === 1) {
            content.push(<div>{last(hierarchy[0])}</div>);
          } else if (hierarchy.length === 2) {
            content.push(
              <Link target="_blank" href={`/dimensionlists/${first(hierarchy[1])}/${listType}`}>
                {last(hierarchy[1])}
                <br />
              </Link>,
            );
          } else if (hierarchy.length > 2) {
            const nestedList = drop(hierarchy, 2);
            const nestedListToolTip = nestedList.map(nestedItem => (
              <Link
                target="_blank"
                key={first(nestedItem)}
                href={`/dimensionlists/${first(nestedItem)}/${listType}`}
              >
                {last(nestedItem)} <br />
              </Link>
            ));
            content.push(
              <TooltipHost
                className={styles.tooltipContent}
                content={nestedListToolTip}
                closeDelay={1000}
                calloutProps={{
                  gapSpace: 5,
                }}
              >
                <Link target="_blank" href={`/dimensionlists/${first(hierarchy[1])}/${listType}`}>
                  {last(hierarchy[1])} <span> (nested)</span> <br />
                </Link>
              </TooltipHost>,
            );
          }
        });
        return <div className={styles.productText}>{content}</div>;
      },
    },
    associatedListCreateList: {
      key: 'associatedList',
      name: 'Associated List',
      fieldName: 'associatedList',
      columnActionsMode: 0,
      isMultiline: true,
      minWidth: 100,
      maxWidth: 300,
      isPadded: true,
      onRender: item => {
        const listHierarchy = map(get(item, 'listHierarchy'));
        const content = [];
        listHierarchy.forEach(list => {
          const hierarchy = Object.entries(list);
          if (last(hierarchy[0]) === 'Directly Added to List' || first(hierarchy[0]) === '0') {
            content.push(<div>{last(hierarchy[0])}</div>);
          } else {
            content.push(
              <Link target="_blank" href={`/dimensionlists/${first(hierarchy[0])}/${listType}`}>
                {last(hierarchy[0])}
                <br />
              </Link>,
            );
          }
        });
        return <div className={styles.productText}>{content}</div>;
      },
    },
  };

  const dimensionItemsColumns = [columns.itemName, columns.tokenId, columns.associatedList];
  const productItemColumns = [
    columns.itemName,
    columns.productId,
    columns.tokenId,
    columns.associatedList,
  ];
  const createProductListColumns = [
    columns.itemName,
    columns.productId,
    columns.tokenId,
    columns.associatedListCreateList,
  ];
  const columnSelection = () => {
    if (createList) return createProductListColumns;
    if (listType === 'product') return productItemColumns;
    return dimensionItemsColumns;
  };
  return (
    <>
      {dimensionItems && dimensionItems.length > 0 && (
        <DetailsList
          items={dimensionItems.map(listItem => ({
            ...listItem,
            key: listItem.id,
          }))}
          columns={columnSelection()}
          {...other}
        />
      )}
    </>
  );
};

DimensionListsDetails.propTypes = {
  listName: PropTypes.string,
  listType: PropTypes.string,
  dimensionItems: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
    }),
  ),
  createList: PropTypes.bool,
};

DimensionListsDetails.defaultProps = {
  listName: '',
  listType: null,
  dimensionItems: [],
  createList: false,
};

export default DimensionListsDetails;
